import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CarrierFormDialog } from '../components/CarrierFormDialog';
import { carrierAPI } from '../api';
import { toast } from 'sonner';
import { Plus, Search, Mail, Phone } from 'lucide-react';

const Carriers = () => {
  const [carriers, setCarriers] = useState([]);
  const [filteredCarriers, setFilteredCarriers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    fetchCarriers();
  }, []);

  useEffect(() => {
    const filtered = carriers.filter(c => 
      (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.mc_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.dot_number || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCarriers(filtered);
  }, [searchTerm, carriers]);

  const fetchCarriers = async () => {
    try {
      const response = await carrierAPI.getAll();
      setCarriers(response.data);
      setFilteredCarriers(response.data);
    } catch (error) {
      toast.error('Failed to load carriers');
    } finally {
      setLoading(false);
    }
  };


  return (
    <MainLayout>
      <div className="" data-testid="carriers-page">
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--tms-text)]">Carriers</h1>
            <p className="text-sm md:text-base text-[var(--tms-text-secondary)] mt-1">Manage your carrier network</p>
          </div>
          <Button data-testid="create-carrier-button" size="sm" className="w-full sm:w-auto" onClick={() => setShowDialog(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add Carrier
          </Button>
          <CarrierFormDialog open={showDialog} onOpenChange={setShowDialog} onCreated={fetchCarriers} />
        </div>


        <div className="stats-card">
          {loading ? (
            <div className="text-center py-12 text-[var(--tms-text-secondary)]">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr><th>Carrier Name</th><th>MC Number</th><th>Contact</th><th>On-Time Pickup</th><th>On-Time Delivery</th></tr>
                </thead>
                <tbody>
                  {filteredCarriers.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-12 text-[var(--tms-text-muted)]">{searchTerm ? 'No carriers found' : 'No carriers yet. Add your first carrier to get started.'}</td></tr>
                  ) : (
                    filteredCarriers.map((carrier) => (
                      <tr key={carrier.id} data-testid={`carrier-row-${carrier.id}`}>
                        <td>
                          <Link to={`/carriers/${carrier.id}`} className="font-semibold text-[var(--tms-primary)] hover:text-blue-700" data-testid={`carrier-link-${carrier.id}`}>
                            {carrier.name}
                          </Link>
                        </td>
                        <td className="font-mono text-sm">{carrier.mc_number}</td>
                        <td>
                          <div className="flex flex-col gap-1">
                            <a 
                              href={`mailto:${carrier.contact_email}`} 
                              className="flex items-center gap-1 text-sm text-[var(--tms-primary)] hover:text-blue-700"
                              data-testid={`email-${carrier.id}`}
                            >
                              <Mail className="w-3 h-3" /> {carrier.contact_email}
                            </a>
                            <a 
                              href={`tel:${carrier.contact_phone}`} 
                              className="flex items-center gap-1 text-sm text-[var(--tms-success)] hover:text-green-700"
                              data-testid={`phone-${carrier.id}`}
                            >
                              <Phone className="w-3 h-3" /> {carrier.contact_phone}
                            </a>
                          </div>
                        </td>
                        <td className="font-mono">{carrier.on_time_pickup_pct.toFixed(1)}%</td>
                        <td className="font-mono">{carrier.on_time_delivery_pct.toFixed(1)}%</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Carriers;